import type { ShipId } from './ships.js';

export type WeaponType = 'beam' | 'pulse' | 'laser' | 'torpedo' | 'missile';

/** One barrel entry for multi-barrel or spread-fire weapons. */
export interface BarrelConfig {
  /** Lateral offset in ship-local pixels (positive = right). */
  localX: number;
  /** Longitudinal offset in ship-local pixels (negative = forward, positive = backward). */
  localY: number;
  /** Additional firing angle in radians, relative to ship heading. */
  angleOffset: number;
}

export interface WeaponDefinition {
  type: WeaponType;
  /** Damage applied per hit (per projectile for multi-barrel weapons) */
  damage: number;
  /** Energy drained per full volley */
  energyCost: number;
  /** Seconds between volleys (e.g. 0.15 = 150 ms cooldown) */
  cooldownSecs: number;
  /** Maximum range in world pixels */
  range: number;
  /** Projectile travel speed px/s (pulse/laser only; unused for beam) */
  projectileSpeed: number;
  /** Hex colour used by the client renderer */
  color: number;
  /**
   * Fraction of damage that reaches armour (after shield phase).
   * 1.0 = full armour damage (default — collisions, explosions, secondaries).
   * 0.3 = primary weapons, which strip shields well but barely scratch armour.
   */
  armorDamageMult?: number;
  /**
   * Per-barrel spawn config.  If omitted, a single projectile fires from ship
   * centre with no angle offset (original behaviour).
   */
  barrels?: BarrelConfig[];
}

export interface SecondaryWeaponDefinition extends WeaponDefinition {
  /** Max magazine capacity */
  magazine: number;
  /** Rounds reloaded per second (e.g. 1/14 = one round every 14 s) */
  reloadRate: number;
  /**
   * Damage multiplier used while the target still has shields.
   * 0.25 means the weapon deals one quarter damage until shields collapse;
   * it cannot bypass the shield, though reduced overflow can reach armor.
   */
  shieldDamageMultiplier: number;
  /**
   * Maximum turn rate in radians/second toward the nearest enemy.
   * 0 = unguided.  Torpedoes have weak guidance; missiles have strong guidance.
   */
  homingStrength: number;
}

const DEG = Math.PI / 180;

/**
 * Primary weapons assigned to each ship class.
 *
 * Primaries strip shields efficiently, while their armour multipliers and
 * engagement ranges give each hull a distinct role.
 *
 * Fighter    — triple parallel turbolaser; dependable all-rounder
 * RoboTAI    — mouse-aimed hitscan beam; steady tracking damage
 * Juggernaut — five-shot spread; devastating only at close range
 * Wraith     — long-range phase cannon; precise and armour-piercing
 * Corsair    — tight three-shot scattergun; close-range raider
 * Lancer     — slow-firing heavy pulse; long-range artillery
 * Skimmer    — twin ion repeaters; sustained shield pressure
 */
export const PRIMARY_WEAPONS: Record<ShipId, WeaponDefinition> = {
  fighter: {
    type: 'laser',
    damage: 2.5,
    energyCost: 6,
    cooldownSecs: 0.17,
    range: 850,
    projectileSpeed: 950,
    color: 0x44aaff,
    armorDamageMult: 0.4,
    barrels: [
      { localX:   0, localY: -30, angleOffset: 0 },  // nose
      { localX: -22, localY:  -8, angleOffset: 0 },  // left wing
      { localX:  22, localY:  -8, angleOffset: 0 },  // right wing
    ],
  },
  robotai: {
    type: 'beam',
    damage: 4.5,
    energyCost: 7,
    cooldownSecs: 0.17,
    range: 760,
    projectileSpeed: 0,
    color: 0xff3333,
    armorDamageMult: 0.45,
  },
  juggernaut: {
    type: 'laser',
    damage: 4.5,
    energyCost: 18,
    cooldownSecs: 0.34,
    range: 680,
    projectileSpeed: 800,
    color: 0xff4400,
    armorDamageMult: 0.4,
    barrels: [
      { localX: 0, localY: -15, angleOffset: -12 * DEG },
      { localX: 0, localY: -15, angleOffset:  -6 * DEG },
      { localX: 0, localY: -15, angleOffset:   0 },
      { localX: 0, localY: -15, angleOffset:   6 * DEG },
      { localX: 0, localY: -15, angleOffset:  12 * DEG },
    ],
  },
  wraith: {
    type: 'laser',
    damage: 6.5,
    energyCost: 6,
    cooldownSecs: 0.14,
    range: 1100,
    projectileSpeed: 1250,
    color: 0x00ff88,
    armorDamageMult: 0.75,
    barrels: [
      { localX: 0, localY: -28, angleOffset: 0 },  // single nose barrel
    ],
  },
  corsair: {
    type: 'laser',
    damage: 4.5,
    energyCost: 10,
    cooldownSecs: 0.28,
    range: 600,
    projectileSpeed: 820,
    color: 0xff3355,
    armorDamageMult: 0.55,
    barrels: [
      { localX: -18, localY: -18, angleOffset: -5 * DEG },
      { localX:   0, localY: -24, angleOffset:  0 },
      { localX:  18, localY: -18, angleOffset:  5 * DEG },
    ],
  },
  lancer: {
    type: 'pulse',
    damage: 18,
    energyCost: 13,
    cooldownSecs: 0.50,
    range: 1300,
    projectileSpeed: 1400,
    color: 0xbb66ff,
    armorDamageMult: 0.65,
    barrels: [{ localX: 0, localY: -34, angleOffset: 0 }],
  },
  skimmer: {
    type: 'pulse',
    damage: 3.4,
    energyCost: 8,
    cooldownSecs: 0.25,
    range: 820,
    projectileSpeed: 900,
    color: 0x33ddff,
    armorDamageMult: 0.45,
    barrels: [
      { localX: -22, localY: -12, angleOffset: 0 },
      { localX:  22, localY: -12, angleOffset: 0 },
    ],
  },
};

/**
 * Secondary weapons — limited-magazine torpedoes and missiles.
 *
 * Every base magazine carries roughly 140 points of armour damage. Hulls trade
 * burst size, tracking, and reload cadence rather than raw magazine lethality.
 * Secondaries are reduced to 25% before a remaining shield absorbs the hit.
 */
export const SECONDARY_WEAPONS: Record<ShipId, SecondaryWeaponDefinition> = {
  fighter: {
    type: 'torpedo',
    damage: 36,
    energyCost: 6,
    cooldownSecs: 0.65,
    range: 1350,
    projectileSpeed: 700,
    color: 0x00ffcc,
    magazine: 4,
    reloadRate: 1 / 8,
    shieldDamageMultiplier: 0.25,
    homingStrength: 0.8,
  },
  robotai: {
    type: 'missile',
    damage: 27,
    energyCost: 4,
    cooldownSecs: 0.75,
    range: 1600,
    projectileSpeed: 600,
    color: 0xff6600,
    magazine: 5,
    reloadRate: 1 / 9,
    shieldDamageMultiplier: 0.25,
    homingStrength: 2.3,
  },
  juggernaut: {
    type: 'torpedo',
    damage: 50,
    energyCost: 8,
    cooldownSecs: 0.85,
    range: 1100,
    projectileSpeed: 500,
    color: 0xffff00,
    magazine: 3,
    reloadRate: 1 / 12,
    shieldDamageMultiplier: 0.25,
    homingStrength: 0.6,
  },
  wraith: {
    type: 'torpedo',
    damage: 34,
    energyCost: 5,
    cooldownSecs: 0.65,
    range: 1600,
    projectileSpeed: 900,   // fastest torpedo in the game
    color: 0x00ff88,
    magazine: 4,
    reloadRate: 1 / 9,
    shieldDamageMultiplier: 0.25,
    homingStrength: 1.1,
  },
  corsair: {
    type: 'missile',
    damage: 34,
    energyCost: 5,
    cooldownSecs: 0.60,
    range: 1050,
    projectileSpeed: 720,
    color: 0xff3355,
    magazine: 4,
    reloadRate: 1 / 9,
    shieldDamageMultiplier: 0.25,
    homingStrength: 1.4,
  },
  lancer: {
    type: 'missile',
    damage: 28,
    energyCost: 5,
    cooldownSecs: 0.80,
    range: 1700,
    projectileSpeed: 650,
    color: 0xbb66ff,
    magazine: 5,
    reloadRate: 1 / 10,
    shieldDamageMultiplier: 0.25,
    homingStrength: 1.8,
  },
  skimmer: {
    type: 'missile',
    damage: 22,
    energyCost: 4,
    cooldownSecs: 0.70,
    range: 1450,
    projectileSpeed: 580,
    color: 0x33ddff,
    magazine: 6,
    reloadRate: 1 / 10,
    shieldDamageMultiplier: 0.25,
    homingStrength: 2.6,
  },
};
